import React, { useEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";
import { useImmer } from "use-immer";

import { KnowledgeListFilters } from "@intelligems/ig-types";
import { useGetKnowledgeCategories } from "@src/api/ai/knowledge/use-get-knowledge-categories";
import { useGetKnowledgeRootCauses } from "@src/api/ai/knowledge/use-get-knowledge-root-causes";
import { useGetKnowledgeSymptoms } from "@src/api/ai/knowledge/use-get-knowledge-symptoms";
import { Processes } from "@src/components/MainApp/Alerts/Processes";
import { KnowledgeSideNav } from "@src/components/Navigation/KnowledgeSideNav/KnowledgeSideNav";
import { IgPageContent } from "@src/utils/IgPageContent";

import { AlertBanner } from "./alerts/AlertBanner";
import { AppLayoutShell } from "./layout/AppLayoutShell";

const DEFAULT_SOURCE = "all";

export const KnowledgePageProvider = () => {
  const [filters, setFilters] = useImmer<KnowledgeListFilters>({
    search: "",
    status: "all",
    categories: [],
    symptoms: [],
    rootCauses: [],
    source: DEFAULT_SOURCE,
    page: 1,
    limit: 10000,
    sortBy: "updatedAt",
    sortDirection: "desc",
  });

  const source = filters.source === "all" ? undefined : filters.source;
  const categoriesQuery = useGetKnowledgeCategories(source);
  const categories = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data]
  );

  const symptomsQuery = useGetKnowledgeSymptoms(source);
  const symptoms = useMemo(
    () => symptomsQuery.data ?? [],
    [symptomsQuery.data]
  );

  const rootCausesQuery = useGetKnowledgeRootCauses(source);
  const rootCauses = useMemo(
    () => rootCausesQuery.data ?? [],
    [rootCausesQuery.data]
  );

  useEffect(() => {
    if (categories.length === 0 || filters.categories.length > 0) {
      return;
    }
    setFilters((draft) => {
      draft.categories = [...categories];
    });
  }, [categories, filters.categories.length, setFilters]);

  return (
    <AppLayoutShell
      banner={<AlertBanner />}
      sideNav={
        <KnowledgeSideNav
          filters={filters}
          categories={categories}
          symptoms={symptoms}
          rootCauses={rootCauses}
          setFilters={setFilters}
        />
      }
      maxWidth={1600}
    >
      <IgPageContent>
        <Processes />
        <Outlet
          context={{
            filters,
            setFilters,
            categories,
            symptoms,
            rootCauses,
          }}
        />
      </IgPageContent>
    </AppLayoutShell>
  );
};
